import React, { useEffect, useState } from 'react';
import block from 'bem-cn';
import QRCode from 'qrcode';

import Button from 'components/Button';
import SVG from 'components/SVG';

import formateNumber from 'shared/helpers/formateNumber';
import { MAX_PAYMENT_AMOUNT, PAYMENT_NETWORK, PAYMENT_TIMER_SECONDS, formatTimer } from 'features/finance/data';
import useCountdown from '../../hooks/useCountdown';

import copySVG from './img/copy.svg';

import './PurchaseStep.scss';

const b = block('payments-purchase');

const copyText = text => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text);
    return;
  }

  const area = document.createElement('textarea');
  area.value = text;
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  document.body.removeChild(area);
};

const Field = ({ label, value, onCopy }) => (
  <div className={b('field')}>
    <div className={b('label')}>{label}</div>
    <div className={b('value', { copyable: Boolean(onCopy) })}>
      <span className={b('value-text')}>{value}</span>
      {onCopy && (
        <button type="button" className={b('copy')} onClick={onCopy}>
          <SVG svgProps={{ svg: copySVG }} className={b('copy-icon')} />
        </button>
      )}
    </div>
  </div>
);

const PurchaseStep = ({
  amount,
  walletAddress = '',
  qrUrl,
  timerSeconds = PAYMENT_TIMER_SECONDS,
  onSubmit,
  onExpire,
  locale,
  maxDeposit = MAX_PAYMENT_AMOUNT,
  isProcessing = false,
}) => {
  const [qrSrc, setQrSrc] = useState(qrUrl || '');
  const secondsLeft = useCountdown(timerSeconds, onExpire);
  const formattedMaxDeposit = formateNumber(maxDeposit, true);

  useEffect(() => {
    if (qrUrl) {
      setQrSrc(qrUrl);
      return;
    }

    if (!walletAddress) return undefined;

    let isMounted = true;

    QRCode.toDataURL(walletAddress, { width: 360, margin: 1 })
      .then(url => {
        if (isMounted) setQrSrc(url);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [qrUrl, walletAddress]);

  const handleSubmit = event => {
    event.preventDefault();
    if (isProcessing || secondsLeft <= 0) return;
    onSubmit();
  };

  return (
    <form className={b()} onSubmit={handleSubmit}>
      <h1 className={b('title')}>{locale.balancePurchase}</h1>

      <div className={b('main')}>
        <div className={b('info')}>
          <p className={b('section')}>{locale.topUpInformation}</p>
          <Field label={locale.paymentNetwork} value={PAYMENT_NETWORK} />
          <Field label={locale.topUpAddress} value={walletAddress} onCopy={() => copyText(walletAddress)} />
          <Field label={locale.topUpAmountUsdt} value={amount} />
          <p className={b('note')}>{locale.forfeitNote.replace('{amount}', formattedMaxDeposit)}</p>

          <div className={b('timer')}>
            <span>{locale.paymentTimer}</span>
            <span>{formatTimer(secondsLeft)}</span>
          </div>
        </div>

        <div className={b('qr')}>{qrSrc && <img className={b('qr-image')} alt="" src={qrSrc} />}</div>
      </div>

      <p className={b('instruction')}>{locale.sendUsdtInstruction.replace('{amount}', amount)}</p>

      <Button type="submit" text={locale.iSent} size="high" upperCased disabled={isProcessing || secondsLeft <= 0} />
    </form>
  );
};

export default PurchaseStep;
